import { Component, OnInit, AfterViewInit, OnDestroy, Input, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { MatDrawer, MatSnackBar, MatSort, MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material';
import { MatPaginator } from '@angular/material/paginator'
import { BaseComponent } from 'src/app/common/components/base/base.component';
import { LoginService } from 'src/app/managers/login.service';
import { Subscription } from 'rxjs';
import { EditUserComponent } from '../edit-user/edit-user.component';
import { CoreResponseStatus } from 'src/app/contracts/responseMessage/coreResponseMessage';
import { AddUserComponent } from 'src/app/components/add-user/add-user.component';
import { ClientConfigService } from 'src/app/common/services/client-config.service';

@Component({
  selector: 'app-admin-users',
  templateUrl: './admin-users.component.html',
  styleUrls: ['./admin-users.component.css']
})
export class AdminUsersComponent extends BaseComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() drawer: MatDrawer;
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
  @ViewChild(MatSort, { static: true }) sort: MatSort;

  tableDisplayColumns;
  totalUserCount: number;
  loading: boolean;
  private _pageIndex: number;
  private _pageSize: number;
  private _sortBy: string;
  private _orderBy: string;
  private _paginatorSubscription: Subscription;
  private _sortSubscription: Subscription;

  constructor(route: ActivatedRoute, loginManager: LoginService, clientConfig: ClientConfigService, dialog: MatDialog, private _snackBar: MatSnackBar) {
    super(route, loginManager, clientConfig, dialog);        
  }

  ngOnInit() {
    super.ngOnInit();

    this._pageIndex = 0;
    this._pageSize = 5;
    this.paginator.pageSize = this._pageSize;
    this.paginator.pageIndex = this._pageIndex;  
    this._sortBy = "Email";
    this._orderBy = "ASC";
    this.loadDataSource();
    this.tableDisplayColumns = ['Email', 'Role', 'IsLoggedIn', 'Active', 'LastActivityTime', 'CreatedDate', 'Id'];  
  }

  ngAfterViewInit() {     
    //subscribe to get changes in paginator component in order to load data accordingly.
    this._paginatorSubscription = this.paginator.page.subscribe(() => {
      this._pageIndex = this.paginator.pageIndex;
      this._pageSize = this.paginator.pageSize;
      this.loadDataSource();           
    });   
    
    this._sortSubscription = this.sort.sortChange.subscribe(() => {      
      this._sortBy = this.sort.active;
      this._orderBy = this.sort.direction.toString().toUpperCase();
      this.loadDataSource();
    })
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    this._paginatorSubscription.unsubscribe();
    this._sortSubscription.unsubscribe();
  }

  toggleDrawer() {
    this.drawer.toggle();  
  }

  onUserEditClick(user) {    
    const dialogRef = this.dialog.open(EditUserComponent, {
      width: '700px',
      height: '450px',    
      data: { 
        parentComponent: 'AdminUsers',
        dataSource: user
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result !== null)            
        this.loadDataSource();       
    });
  }

  onUserDeleteClick(user) {
    const alertRef = this.openAlertDialog('Confirmation', 'Do you want to continue to delete the user \'' + user.Email + '\'?', '400px', '200px', 'Delete');

    alertRef.afterClosed().subscribe(result => {
      if (result === 'Delete')
        this.removeUsers([user.Id]);
    });  
  }

  onUserAddClick() {
    const dialogRef = this.dialog.open(AddUserComponent, {
      width: '700px',
      height: '300px',
      data: {
        parentComponent: 'AdminUsers'
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result !== null)
        this.loadDataSource();
    })
  }

  private async removeUsers(deleteUserIds: number[]) {
    this._snackBar.open('Deleting...');
    let responseMsg = await this.loginManager.removeUserAsync(this.getLoggedinUser(), deleteUserIds);
    this._snackBar.dismiss();    

    if (responseMsg.Status === CoreResponseStatus.OK) {
      this._snackBar.open('Success to delete the user.', 'OK', { duration: this._successMsgDuration });
      this.loadDataSource();      
    }
    else {
      this._snackBar.open('Failed to delete the user: ' + responseMsg.Message, 'OK', { duration: this._failMsgDuration });
      this.handleCoreResponseMessageError(responseMsg);
    }
  }

  private async loadDataSource() {    
    this.loading = true;
    let responseMsg = await this.loginManager.getAllSync(this.getLoggedinUser(), this._pageIndex, this._pageSize, this._sortBy, this._orderBy);    

    if (responseMsg.Status === CoreResponseStatus.OK) {            
      this.dataSource = responseMsg.Result;    
      this.totalUserCount = responseMsg.TotalDataCount;
      this.loading = false;          
    }
    else {
      this.handleCoreResponseMessageError(responseMsg);
      this.loading = false;
      this._snackBar.open('Failed to load user data: ' + responseMsg.Message, 'OK');      
    }
  }
}